/**
 * Handles the notifications, alerts and confirmation prompts from the {@link uimessages} service.
 */
class UIMessagesContainer {
  /**
   * @param {ngInterval} $interval  When a notification is created, a `new` flag is set on it so
   *                                the child component can differentiate it. This service is
   *                                used to remove the flag after its _"fresheness"_ expires.
   * @param {UIMessages} uimessages To listen for new notifications, alerts and prompts.
   */
  constructor($interval, uimessages) {
    'ngInject';

    /**
     * A local reference for the `$interval` service.
     * @type {ngInterval}
     */
    this.$interval = $interval;
    /**
     * A local reference for the {@link uimessages} service.
     * @type {UIMessages}
     */
    this.uimessages = uimessages;
    /**
     * Once the container gets mounted, this list will contain all the unsubscribe functions the
     * {@link uimessages} service returns, so they can all be removed when the container is
     * unmounted.
     * @type {Array}
     */
    this.unsubscribers = [];
    /**
     * In milliseconds, for how much time a notification should be considered as _"new"_.
     * @type {number}
     */
    this.notificationsFreshnessTime = 1200;
    /**
     * In milliseconds, for how much time an expired notification should be kept on the
     * `notifications` list before removing it. When a notification expires, its `expired`
     * property is set to `true` so the child component can differentiate it before removing it.
     * @type {number}
     */
    this.notificationsExpirationDelayTime = 1200;
    /**
     * The list of notifications.
     * @type {Array}
     */
    this.notifications = [];
    /**
     * Whether or not to show an alert window.
     * @type {boolean}
     */
    this.showAlert = false;
    /**
     * The information for the alert window that is being shown.
     * @type {Object}
     */
    this.alertInfo = {};
    /**
     * Whether or not to show a confirmation prompt.
     * @type {boolean}
     */
    this.showConfirm = false;
    /**
     * The information for the confirmation prompt that is being shown.
     * @type {Object}
     */
    this.confirmInfo = {};
    /**
     * Bind it so it can be sent to the {@link uimessages} service as reference.
     * @ignore
     */
    this.addNotification = this.addNotification.bind(this);
    /**
     * Bind it so it can be sent to the {@link uimessages} service as reference.
     * @ignore
     */
    this.closeNotification = this.closeNotification.bind(this);
    /**
     * Bind it so it can be sent to the {@link uimessages} service as reference.
     * @ignore
     */
    this.openAlert = this.openAlert.bind(this);
    /**
     * Bind it so it can be sent to the {@link uimessages} service as reference.
     * @ignore
     */
    this.openConfirm = this.openConfirm.bind(this);
  }
  /**
   * When the container gets mounted, start listening for the {@link uimessages} service events.
   */
  $onInit() {
    this.unsubscribers = [
      this.uimessages.onNewNotification(this.addNotification),
      this.uimessages.onExpiredNotification(this.closeNotification),
      this.uimessages.onNewAlert(this.openAlert),
      this.uimessages.onNewConfirm(this.openConfirm),
    ];
  }
  /**
   * When the container gets destroyed, remove all the listeners from the {@link uimessages}
   * service.
   */
  $onDestroy() {
    this.unsubscribers.forEach((fn) => fn());
  }
  /**
   * This gets called when a new notification is added to the {@link uimessages} service.
   * @param {Object} notification The notification information.
   */
  addNotification(notification) {
    // Add the custom `new` and `expired` flag the container and child component use.
    const newNotification = Object.assign({}, notification, {
      new: true,
      expired: false,
    });
    // Push it to the list.
    this.notifications.push(newNotification);
    // Set the time to remove the `new` flag.
    let timer = this.$interval(() => {
      this._notificationById(newNotification.id).new = false;
      this.$interval.cancel(timer);
      timer = null;
    }, this.notificationsFreshness);
  }
  /**
   * This is called from the child component when the alert is closed.
   */
  closeAlert() {
    this.alertInfo.deferred.resolve();
    this.showAlert = false;
    this.alertInfo = {};
  }
  /**
   * This is called when the user closes a notification from the child component and/or when
   * the notification expires on the {@link uimessages} service.
   * @param {Object} notification The notification information.
   */
  closeNotification(notification) {
    const expiredNotification = this._notificationById(notification.id);
    /**
     * We need this check because it's possible the user manually closed the
     * notification before the expiration time.
     */
    if (expiredNotification) {
      // Set the expired flag to true.
      expiredNotification.expired = true;
      // Set a timer to remove the notification from the list.
      let timer = this.$interval(() => {
        const index = this._notificationIndexById(expiredNotification.id);
        if (index > -1) {
          this.notifications.splice(index, 1);
          this.$interval.cancel(timer);
          timer = null;
        }
      }, this.notificationsExpirationDelayTime);
    }
  }
  /**
   * This is called from the child component when the user either accepts or cancels the prompt.
   * @param {boolean} value Whether the user accepted the confirmation or not.
   */
  handleConfirm(value) {
    this.confirmInfo.deferred.resolve(value);
    this.showConfirm = false;
    this.confirmInfo = {};
  }
  /**
   * This is called when a new alert is created on the {@link uimessages} service.
   * @param {Object} alertInfo The alert information.
   */
  openAlert(alertInfo) {
    this.showAlert = true;
    this.alertInfo = alertInfo;
  }
  /**
   * This is called when a new confirmation prompt is created on the {@link uimessages} service.
   * @param {Object} confirmInfo The prompt information.
   */
  openConfirm(confirmInfo) {
    this.showConfirm = true;
    this.confirmInfo = confirmInfo;
  }
  /**
   * Gets a notification from the list by its ID.
   * @param {number} id The notification ID.
   * @return {Object}
   * @access protected
   * @ignore
   */
  _notificationById(id) {
    return this.notifications[this._notificationIndexById(id)];
  }
  /**
   * Gets the index of a notification on the list by its ID.
   * @param {number} id The notification ID.
   * @return {number}
   * @access protected
   * @ignore
   */
  _notificationIndexById(id) {
    return this.notifications.findIndex((notification) => notification.id === id);
  }
}
/**
 * Provides a UI for the notification, alerts and prompts from the {@link uimessages} service.
 * @ngesdoc component
 * @ngname uiMessagesContainer
 * @ngcontroller {@link UIMessagesContainer}
 * @ngcontainer
 * @ngmodule common
 */
export default {
  controller: UIMessagesContainer,
  template: `
    <uimessages
      notifications="$ctrl.notifications"
      on-notification-close-request="$ctrl.closeNotification(notification)"

      show-alert="$ctrl.showAlert"
      alert-info="$ctrl.alertInfo"
      on-alert-action="$ctrl.closeAlert()"

      show-confirm="$ctrl.showConfirm"
      confirm-info="$ctrl.confirmInfo"
      on-confirm-action="$ctrl.handleConfirm(value)"
    ></uimessages>
  `,
};
